import type { ComponentType } from "react";

import type { AccessState, ToolAvailability, ToolId } from "@/features/commandDeck/contracts/types";
import { getToolAvailability } from "@/features/commandDeck/config/toolMatrix";
import type { IconProps } from "@/components/icons/ServiceIcons";
import {
  CoachesMentorsIcon,
  GoalBuddyIcon,
  NFTSKTIcon,
  OmniCastIcon,
  OmniCineIcon,
  OmniStudioIcon,
  SmartSkinsIcon,
  SynkStriveIcon,
} from "@/components/icons/ServiceIcons";
import type { ServiceId } from "@/state/useServiceStatus";

export type ProductNavItem = {
  id: string;
  label: string;
  path: string;
  icon: ComponentType<IconProps>;
  showInTopHeader: boolean;
  showInBottomTabs: boolean;
  showInServiceRail: boolean;
  shortLabel?: string;
  statusId?: ServiceId;
  hue?: number;
  matchPaths?: string[];
};

export const GUIDED_WORLD_FORGE_SURFACE_ID = "guided_world_forge";
export const COMMAND_DECK_SURFACE_ID = "command_deck";

export const PRODUCT_NAV_ITEMS: readonly ProductNavItem[] = [
  {
    id: COMMAND_DECK_SURFACE_ID,
    label: "Command Deck",
    path: "/command-deck",
    icon: SynkStriveIcon,
    showInTopHeader: false,
    showInBottomTabs: false,
    showInServiceRail: false,
    shortLabel: "Deck",
    hue: 212,
    matchPaths: ["/command-deck/preview"],
  },
  {
    id: "goalbuddy",
    label: "GoalBuddy",
    path: "/goalbuddy",
    icon: GoalBuddyIcon,
    showInTopHeader: true,
    showInBottomTabs: true,
    showInServiceRail: true,
    statusId: "goalbuddy",
    hue: 200,
  },
  {
    id: "omnicast",
    label: "OmniCast",
    path: "/omnicast",
    icon: OmniCastIcon,
    showInTopHeader: true,
    showInBottomTabs: true,
    showInServiceRail: true,
    statusId: "omnicast",
    hue: 195,
  },
  {
    id: "omnicine",
    label: "OmniCine",
    path: "/omnicine",
    icon: OmniCineIcon,
    showInTopHeader: true,
    showInBottomTabs: true,
    showInServiceRail: true,
    statusId: "omnicine",
    hue: 280,
  },
  {
    id: "omnistudio",
    label: "OmniStudio",
    path: "/omnistudio",
    icon: OmniStudioIcon,
    showInTopHeader: true,
    showInBottomTabs: true,
    showInServiceRail: true,
    hue: 140,
  },
  {
    id: GUIDED_WORLD_FORGE_SURFACE_ID,
    label: "Start From World",
    path: "/guided/start-from-world",
    icon: OmniStudioIcon,
    showInTopHeader: false,
    showInBottomTabs: false,
    showInServiceRail: false,
    shortLabel: "World",
    hue: 198,
  },
  {
    id: "guided_studio",
    label: "Guided Studio",
    path: "/guided-studio",
    icon: OmniStudioIcon,
    showInTopHeader: false,
    showInBottomTabs: false,
    showInServiceRail: false,
    shortLabel: "Guided Studio",
    hue: 188,
  },
  {
    id: "synkstrive",
    label: "SynkStrive",
    path: "/synkstrive",
    icon: SynkStriveIcon,
    showInTopHeader: true,
    showInBottomTabs: false,
    showInServiceRail: false,
    statusId: "synkstrive",
    hue: 140,
  },
  {
    id: "coaches",
    label: "Coaches & Mentors",
    path: "/coaches",
    icon: CoachesMentorsIcon,
    showInTopHeader: true,
    showInBottomTabs: false,
    showInServiceRail: true,
    shortLabel: "Coaches",
    statusId: "coaches",
    hue: 225,
    matchPaths: ["/coaches-mentors"],
  },
  {
    id: "smartskins",
    label: "SmartSkins / Tattoos",
    path: "/smartskins",
    icon: SmartSkinsIcon,
    showInTopHeader: true,
    showInBottomTabs: false,
    showInServiceRail: true,
    shortLabel: "SmartSkins",
    statusId: "smart-skins",
    hue: 170,
    matchPaths: ["/smart-skins", "/smart-tattoos"],
  },
  {
    id: "wearables_hub",
    label: "Wearables",
    path: "/wearables/connect",
    icon: SmartSkinsIcon,
    showInTopHeader: true,
    showInBottomTabs: false,
    showInServiceRail: true,
    shortLabel: "Wearables",
    hue: 188,
    matchPaths: ["/wearables"],
  },
  {
    id: "wallet",
    label: "Wallet & Rewards",
    path: "/wallet",
    icon: GoalBuddyIcon,
    showInTopHeader: true,
    showInBottomTabs: true,
    showInServiceRail: false,
    shortLabel: "Wallet",
    hue: 48,
  },
  {
    id: "nft",
    label: "NFT / SKT",
    path: "/nft",
    icon: NFTSKTIcon,
    showInTopHeader: true,
    showInBottomTabs: false,
    showInServiceRail: true,
    shortLabel: "NFT",
    statusId: "nft",
    hue: 315,
  },
] as const;

export type ProductNavId = (typeof PRODUCT_NAV_ITEMS)[number]["id"];
export const PRODUCT_SURFACE_PATHS: Readonly<Record<string, string>> = Object.freeze({
  ...Object.fromEntries(PRODUCT_NAV_ITEMS.map((item) => [item.id, item.path])),
  [GUIDED_WORLD_FORGE_SURFACE_ID]: "/guided/start-from-world",
});

export type AuthoritySurfaceId =
  | typeof COMMAND_DECK_SURFACE_ID
  | "omnicast"
  | "omnicine"
  | "omnistudio"
  | typeof GUIDED_WORLD_FORGE_SURFACE_ID
  | "guided_studio"
  | "wearables_hub";

export function getTopHeaderProductNavItems(): ProductNavItem[] {
  return PRODUCT_NAV_ITEMS.filter((item) => item.showInTopHeader);
}

export const AUTHORITY_SURFACE_IDS: ReadonlySet<ProductNavId> = new Set([
  COMMAND_DECK_SURFACE_ID,
  "omnicast",
  "omnicine",
  "omnistudio",
  GUIDED_WORLD_FORGE_SURFACE_ID,
  "guided_studio",
  "wearables_hub",
]);

/** Full engine authority rail order (all tabs visible; entitlement shown as status chips). */
const AUTHORITY_SURFACE_RAIL_ORDER: readonly ProductNavId[] = [
  COMMAND_DECK_SURFACE_ID,
  "guided_studio",
  GUIDED_WORLD_FORGE_SURFACE_ID,
  "omnicast",
  "omnicine",
  "omnistudio",
  "wearables_hub",
];

/** Tool matrix keys used to resolve rail gate posture per authority surface. */
const AUTHORITY_SURFACE_TOOL_ID: Partial<Record<AuthoritySurfaceId, ToolId>> = {
  guided_studio: "guided_studio",
  [GUIDED_WORLD_FORGE_SURFACE_ID]: "scapes_worlds",
  omnicast: "omnicast",
  omnicine: "omnicine",
  omnistudio: "omnistudio",
  wearables_hub: "xr_worlds",
};

export type AuthoritySurfaceRailGate =
  | "active"
  | "beta"
  | "preview"
  | "coming_soon"
  | "locked";

export type AuthoritySurfaceRailEntry = {
  item: ProductNavItem;
  gate: AuthoritySurfaceRailGate;
  /** Review cockpit status chip — does not block tab navigation. */
  statusChip: string;
};

export function getAuthoritySurfaceRailStatusChip(gate: AuthoritySurfaceRailGate): string {
  switch (gate) {
    case "active":
      return "Active";
    case "beta":
      return "Beta";
    case "preview":
      return "Preview";
    case "coming_soon":
      return "Coming soon";
    case "locked":
      return "Locked";
    default:
      return "Locked";
  }
}

function availabilityToRailGate(availability: ToolAvailability): AuthoritySurfaceRailGate {
  switch (availability) {
    case "available":
      return "active";
    case "beta":
      return "beta";
    case "preview":
      return "preview";
    case "coming_soon":
      return "coming_soon";
    case "locked":
    case "unavailable":
    case "request_access":
    case "admin_only":
      return "locked";
    default:
      return "locked";
  }
}

export function resolveAuthoritySurfaceRailGate(
  surfaceId: AuthoritySurfaceId,
  accessState: AccessState
): AuthoritySurfaceRailGate {
  if (surfaceId === COMMAND_DECK_SURFACE_ID) {
    return "active";
  }

  const toolId = AUTHORITY_SURFACE_TOOL_ID[surfaceId];
  if (!toolId) {
    return "locked";
  }

  return availabilityToRailGate(getToolAvailability(accessState, toolId));
}

function sortAuthoritySurfaceNavItems(items: ProductNavItem[]): ProductNavItem[] {
  const orderIndex = new Map(AUTHORITY_SURFACE_RAIL_ORDER.map((id, index) => [id, index]));
  return [...items].sort((a, b) => {
    const aOrder = orderIndex.get(a.id) ?? 100;
    const bOrder = orderIndex.get(b.id) ?? 100;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return 0;
  });
}

export function getAuthoritySurfaceNavItems(): ProductNavItem[] {
  const items = PRODUCT_NAV_ITEMS.filter((item) => AUTHORITY_SURFACE_IDS.has(item.id));
  return sortAuthoritySurfaceNavItems(items);
}

/** Full authority rail with per-tab entitlement metadata (all tabs review-navigable). */
export function getAuthoritySurfaceRailEntries(accessState: AccessState): AuthoritySurfaceRailEntry[] {
  return getAuthoritySurfaceNavItems().map((item) => {
    const gate = resolveAuthoritySurfaceRailGate(item.id as AuthoritySurfaceId, accessState);
    return {
      item,
      gate,
      statusChip: getAuthoritySurfaceRailStatusChip(gate),
    };
  });
}

export function getBottomTabProductNavItems(): ProductNavItem[] {
  return PRODUCT_NAV_ITEMS.filter((item) => item.showInBottomTabs);
}

export function getServiceRailProductNavItems(): ProductNavItem[] {
  return PRODUCT_NAV_ITEMS.filter((item) => item.showInServiceRail);
}

export function getProductNavItemById(id: string): ProductNavItem | undefined {
  return PRODUCT_NAV_ITEMS.find((item) => item.id === id);
}

export function isProductNavActive(pathname: string, item: ProductNavItem): boolean {
  const paths = [item.path, ...(item.matchPaths ?? [])];
  return paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function getProductNavItemForPath(pathname: string): ProductNavItem | undefined {
  return PRODUCT_NAV_ITEMS.find((item) => isProductNavActive(pathname, item));
}

export function getAuthoritySurfaceIdForPath(pathname: string): AuthoritySurfaceId | null {
  const activeItem = getProductNavItemForPath(pathname);
  if (!activeItem || !AUTHORITY_SURFACE_IDS.has(activeItem.id)) return null;
  return activeItem.id as AuthoritySurfaceId;
}

export function isAuthoritySurfacePath(pathname: string): boolean {
  return getAuthoritySurfaceIdForPath(pathname) != null;
}